import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from './ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = ({ profile, setProfile, products, setProducts }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [catalogSettings, setCatalogSettings] = useState({
    catalog_type: 'products',
    catalog_title: 'Shop'
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    image_url: '',
    images: [],
    action_text: 'Order Now',
    action_url: ''
  });

  useEffect(() => {
    const catalogType = profile?.catalog_type || 'products';
    setCatalogSettings({
      catalog_type: catalogType,
      catalog_title: profile?.catalog_title || (catalogType === 'services' ? 'Services' : 'Shop')
    });
  }, [profile]);

  const isServices = catalogSettings.catalog_type === 'services';
  const itemLabel = isServices ? 'Service' : 'Product';
  const itemLabelLower = itemLabel.toLowerCase();
  const defaultActionText = isServices ? 'Book Now' : 'Order Now';

  const handleCatalogTypeChange = (catalogType) => {
    setCatalogSettings((current) => {
      const currentDefault = current.catalog_type === 'services' ? 'Services' : 'Shop';
      const nextDefault = catalogType === 'services' ? 'Services' : 'Shop';
      return {
        catalog_type: catalogType,
        catalog_title: !current.catalog_title.trim() || current.catalog_title === currentDefault
          ? nextDefault
          : current.catalog_title
      };
    });
  };

  const saveCatalogSettings = async () => {
    const title = catalogSettings.catalog_title.trim();
    if (!title) {
      toast.error('Public tab title is required');
      return;
    }

    setSavingSettings(true);
    try {
      const response = await axios.put(`${API}/profile`, {
        catalog_type: catalogSettings.catalog_type,
        catalog_title: title
      });
      setProfile(response.data);
      setCatalogSettings((current) => ({ ...current, catalog_title: title }));
      toast.success('Catalogue settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save catalogue settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData({ title: '', description: '', price: '', image_url: '', images: [], action_text: defaultActionText, action_url: '' });
    setIsDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || '',
      price: product.price?.toString() || '',
      image_url: product.image_url || '',
      images: product.images || [],
      action_text: product.action_text || defaultActionText,
      action_url: product.action_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
      image_url: formData.images.length > 0 ? formData.images[0] : formData.image_url,
      images: formData.images,
    };

    try {
      if (editingProduct) {
        const response = await axios.put(`${API}/products/${editingProduct.id}`, payload);
        setProducts(products.map(p => p.id === editingProduct.id ? response.data : p));
        toast.success(`${itemLabel} updated`);
      } else {
        const response = await axios.post(`${API}/products`, payload);
        setProducts([...products, response.data]);
        toast.success(`${itemLabel} added`);
      }
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(`Failed to save ${itemLabelLower}`);
    }
  };

  const handleDelete = async (productId) => {
    try {
      await axios.delete(`${API}/products/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (product) => {
    try {
      const response = await axios.put(`${API}/products/${product.id}`, { is_active: !product.is_active });
      setProducts(products.map(p => p.id === product.id ? response.data : p));
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-5 mb-6">
        <div className="mb-4">
          <h2 className="font-outfit font-semibold text-lg text-white">Catalogue settings</h2>
          <p className="text-sm text-[#A1A1AA] mt-1">Choose what you offer and how this tab appears on your public profile.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#EDEDED]">What do you offer?</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {['products', 'services'].map((catalogType) => (
                <button
                  key={catalogType}
                  type="button"
                  data-testid={`catalog-type-${catalogType}`}
                  onClick={() => handleCatalogTypeChange(catalogType)}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-all ${
                    catalogSettings.catalog_type === catalogType
                      ? 'border-violet-500 bg-violet-500/15 text-violet-300'
                      : 'border-[#27272A] bg-[#1E1E1E] text-[#A1A1AA] hover:text-white'
                  }`}
                >
                  {catalogType === 'services' ? 'Services' : 'Products'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[#EDEDED]">Public tab title</Label>
            <Input
              data-testid="catalog-title-input"
              value={catalogSettings.catalog_title}
              onChange={(event) => setCatalogSettings((current) => ({ ...current, catalog_title: event.target.value }))}
              maxLength={30}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white"
              placeholder={isServices ? 'Services' : 'Shop'}
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            type="button"
            data-testid="save-catalog-settings"
            onClick={saveCatalogSettings}
            disabled={savingSettings}
            className="bg-violet-500 hover:bg-violet-600 text-white"
          >
            {savingSettings ? 'Saving...' : 'Save settings'}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">{isServices ? 'Services' : 'Products'}</h1>
          <p className="text-[#A1A1AA] mt-1">Showcase what you offer</p>
        </div>
        <Button data-testid="add-product-btn" onClick={openCreateDialog} className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2">
          <Plus className="w-4 h-4" /> Add {itemLabel}
        </Button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <AnimatePresence>
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.05 }}
              className={`rounded-2xl bg-[#121212] border border-[#27272A] overflow-hidden ${!product.is_active ? 'opacity-60' : ''}`}
            >
              {/* Image */}
              {(product.images?.length > 0 || product.image_url) ? (
                <div className="h-40 bg-[#1E1E1E] relative">
                  <img src={resolveMediaUrl(product.images?.[0] || product.image_url)} alt={product.title} className="w-full h-full object-cover" 
                    onError={(e) => { e.target.style.display = 'none'; }} />
                  {product.images?.length > 1 && (
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                      {product.images.length} photos
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-40 bg-[#1E1E1E] flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-[#71717A]" />
                </div>
              )}
              
              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-white">{product.title}</h3>
                  {product.price && (
                    <span className="text-violet-400 font-semibold">${product.price}</span>
                  )}
                </div>
                
                {product.description && (
                  <p className="text-sm text-[#A1A1AA] line-clamp-2 mb-3">{product.description}</p>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#71717A]">{product.action_text}</span>
                  
                  <div className="flex items-center gap-1">
                    <button data-testid={`toggle-product-${product.id}`} onClick={() => handleToggle(product)} className="p-1.5 rounded-lg hover:bg-white/5">
                      {product.is_active ? <ToggleRight className="w-5 h-5 text-violet-400" /> : <ToggleLeft className="w-5 h-5 text-[#71717A]" />}
                    </button>
                    <button data-testid={`edit-product-${product.id}`} onClick={() => openEditDialog(product)} className="p-1.5 rounded-lg hover:bg-white/5">
                      <Pencil className="w-4 h-4 text-[#A1A1AA]" />
                    </button>
                    <button data-testid={`delete-product-${product.id}`} onClick={() => handleDelete(product.id)} className="p-1.5 rounded-lg hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {products.length === 0 && (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-white mb-2">No {itemLabelLower}s yet</h3>
          <p className="text-[#A1A1AA] mb-6">Showcase your {itemLabelLower}s</p>
          <Button data-testid="add-first-product-btn" onClick={openCreateDialog} className="rounded-full bg-violet-500 hover:bg-violet-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add your first {itemLabelLower}
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{editingProduct ? `Edit ${itemLabel}` : `Add ${itemLabel}`}</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">{editingProduct ? `Update your ${itemLabelLower} details.` : `Add a new ${itemLabelLower}.`}</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#EDEDED]">Title *</Label>
              <Input data-testid="product-title-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Premium Consulting" required />
            </div>

            <div>
              <Label className="text-[#EDEDED]">Description</Label>
              <Textarea data-testid="product-desc-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder={`Describe your ${itemLabelLower}...`} rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#EDEDED]">Price ($)</Label>
                <Input data-testid="product-price-input" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="99.99" type="number" step="0.01" />
              </div>
              <div>
                <Label className="text-[#EDEDED]">Button Text</Label>
                <Input data-testid="product-action-input" value={formData.action_text} onChange={(e) => setFormData({ ...formData, action_text: e.target.value })}
                  className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder={defaultActionText} />
              </div>
            </div>

            <div>
              <Label className="text-[#EDEDED]">{itemLabel} Images</Label>
              <p className="text-xs text-[#71717A] mt-1 mb-2">Add multiple images (up to 5) — customers can swipe through them</p>
              <div className="space-y-2">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-[#1E1E1E] rounded-lg p-2 border border-[#27272A]">
                    <img src={resolveMediaUrl(img)} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    <span className="text-xs text-[#A1A1AA] flex-1 truncate">Image {idx + 1}</span>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                      className="p-1 rounded-lg hover:bg-red-500/10"><X className="w-4 h-4 text-red-400" /></button>
                  </div>
                ))}
                {formData.images.length < 5 && (
                  <ImageUpload
                    value=""
                    onChange={(url) => { if (url) setFormData(prev => ({ ...prev, images: [...prev.images, url] })); }}
                    label={formData.images.length === 0 ? `Add ${itemLabel} Image` : 'Add Another Image'}
                    shape="rect"
                  />
                )}
              </div>
            </div>

            <div>
              <Label className="text-[#EDEDED]">Action URL</Label>
              <Input data-testid="product-url-input" value={formData.action_url} onChange={(e) => setFormData({ ...formData, action_url: e.target.value })}
                className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://checkout.example.com" />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="bg-transparent border-[#27272A] text-white hover:bg-white/5">Cancel</Button>
              <Button data-testid="save-product-btn" type="submit" className="bg-violet-500 hover:bg-violet-600 text-white">{editingProduct ? 'Save' : `Add ${itemLabel}`}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
